import Scene from "./Scene";

/**
 * @class
 * @memberOf P3
 */
export default class SceneManager {
    /**
     * @param {PIXI.CanvasRenderer|PIXI.WebGLRenderer} renderer
     */
    constructor(renderer) {
        /**
         * @member {Object}
         * @member {signals.Signal} signals.next
         */
        this.signals = {
            next: new signals.Signal()
        };

        /**
         * @member {PIXI.Container}
         * @private
         */
        this._view = new PIXI.Container();

        /**
         * @member {PIXI.CanvasRenderer|PIXI.WebGLRenderer}
         * @private
         */
        this._renderer = renderer;

        /**
         * @member {Object}
         * @private
         */
        this._next = null;

        /**
         * @type {Array.<Scene>}
         * @private
         */
        this._stack = [];
    }

    /**
     * Shows the next Scene. This will destroy all current scenes and remove them from the stack.
     * @param {Scene} scene - The scene to show.
     */
    show(scene) {
        this._next = {};
        this._next.type = 0;
        this._next.scene = scene;
    }

    /**
     * @param {Scene} scene - The scene to add.
     * @param {boolean} [replace] - All other scenes are removed from the display list but will remain in the stack.
     */
    add(scene, replace) {
        this._next = {};
        this._next.type = 1;
        this._next.scene = scene;
        this._next.replace = replace;
    }

    /**
     * @param {Scene} scene - The scene to add.
     * @param {number} [count=1] - The number of scenes to swap with the new scene.
     */
    swap(scene, count) {
        this._next = {};
        this._next.type = 2;
        this._next.scene = scene;
        this._next.removeCount = Math.max(1, count) || 1;
    }

    /**
     * Remove a Scene from the top of the stack.
     * @param {number} [count=1] - The number of scenes to remove from the stack.
     */
    remove(count) {
        this._next = {};
        this._next.type = 2;
        this._next.removeCount = Math.max(1, count) || 1;
    }

    /**
     * Remove all Scenes from the stack.
     */
    removeAll() {
        this._next = {};
        this._next.type = 3;
        this._next.removeCount = Number.MAX_SAFE_INTEGER;
    }

    /**
     * Destroys all Scenes in the stack.
     */
    clear() {
        if (this._transitionInProgress) return;

        for (let i = 0; i < this._stack.length; ++ i) {
            const scene = this._stack[i];
            if (scene.parent) {
                scene.hide();
                scene.parent.removeChild(scene);
            }
            scene.destroy({children: true});
        }
        this._stack.length = 0;
    }

    /**
     * Updates the Scene at the top of the stack.
     */
    update() {
        this._next && this._nextScene();

        if (this._stack.length) {
            this.top.update();
            for (let i = 0; i < this._stack.length - 1; ++ i) {
                const scene = this._stack[i];
                scene.alwaysUpdate && scene.update();
            }
        }
    }

    /**
     * Attempts to resize any children added to the SceneManagers view.
     */
    resize() {
        for (let i = 0; i < this._view.children.length; ++ i) {
            const child = this._view.children[i];
            child.resize && child.resize();
        }
    }

    /**
     * @private
     */
    _nextScene() {
        const next = this._next;
        this._next = null;

        let scene = next.scene;

        if (this.top) {
            this.top.hide();

            switch (next.type) {
                // show
                case 0 : {
                    while (this.top) this._removeTop();
                    break;
                }
                // add
                case 1 : {
                    if (next.replace) {
                        for (let i = this._stack.length - 1; i >= 0; -- i) {
                            const other = this._stack[i];
                            other.parent && other.parent.removeChild(other);
                        }
                    }
                    break;
                }
                // remove
                case 2 : {
                    const count = Math.min(this._stack.length, next.removeCount);
                    for (let i = 0; i < count; ++ i) {
                        this._removeTop();
                    }
                    break;
                }
            }
        }

        if (scene) {
            scene.init();
            this._stack.push(scene);
        } else {
            scene = this.top;
        }

        if (scene) {
            scene.resize();
            !scene.parent && this._view.addChild(scene);

            scene.show();
            if (next.type !== 2 && scene.autoAppear) {
                scene.appear();
            }
        }

        console.log("stack:", this._stack);
        this.signals.next.dispatch(this, scene);
    }

    /**
     * @private
     */
    _removeTop() {
        this.top.parent && this.top.parent.removeChild(this.top);
        this.top.destroy({children: true});
        this.top.unload();
        this._stack.pop();
    }

    /**
     * @returns {PIXI.Container}
     */
    get view() {
        return this._view;
    }

    /**
     * @returns {PIXI.CanvasRenderer|PIXI.WebGLRenderer}
     */
    get renderer() {
        return this._renderer;
    }

    /**
     * The top most Scene in the stack.
     * @returns {Scene}
     */
    get top() {
        return this._stack.length ? this._stack[this._stack.length - 1] : null;
    }
}